import {defineStore} from 'pinia';
import {
  changePass,
  checkTokenInMail,
  getAuthInfo,
  login,
  refreshToken,
  resetPass,
  sendMail,
  signUp,
  updateAccountStatus
} from '@/services';
import {jwtDecode} from 'jwt-decode';
import type {userCredentials} from '@/views/AccountCreation/const';
import {DEFAULT_TRANSPORTER_USER_ID, ROLE} from '@/constants';

export const useAuthStore = defineStore({
  id: 'auth',
  state: () => ({
    authInfo: {},
    userInfo: {},
    token: '',
    roles: [] as Array<string>,
    userId: -1,
    adminId: -1,
    username: '',
  }),
  persist: {
    paths: ['authInfo', 'userInfo', 'token', 'roles', 'userId', 'adminId', 'username']
  },
  getters: {
    userRoles: (state) => state.roles,
    isAdmin: (state) => state.roles.includes(ROLE.ADMIN),
    isDischarger: (state) => state.roles.includes(ROLE.WASTE),
    isTransporter: (state) => state.roles.includes(ROLE.TRANSPORT),
    isDefaultTransporter: (state) => state.userId === DEFAULT_TRANSPORTER_USER_ID
  },
  actions: {
    async getAuthInfo() {
      const authInfo = await getAuthInfo();

      this.authInfo = authInfo;
      let token = localStorage.getItem('token');
      if (token) {
        let decoded: any = jwtDecode(token);
        // remove ROLE_ prefix from roles
        this.roles = decoded['authority'].map((role: any) => role.replace('ROLE_', ''));
        this.username = decoded['sub'];
        this.userId = decoded['userId'];
        this.adminId = decoded['adminId'];
        this.token = token;
      }
    },
    clearCurrentUser() {
      this.authInfo = {};
      this.userInfo = {};
      this.token = '';
      this.roles = [];
      this.userId = -1;
    },
    async login(email: string, password: string) {
      const response: any = await login(email, password);
      const { token } = response;
      localStorage.setItem('token', token);
      localStorage.setItem('token-lifetime', '0');
      localStorage.setItem('token-starttime', String(Date.now()));
      localStorage.setItem('reload', String(Date.now()));
      await this.getAuthInfo();
    },
    async updateToken(token: string) {
      localStorage.setItem('token', token);
      localStorage.setItem('token-lifetime', '0');
      localStorage.setItem('token-starttime', String(Date.now()));
      localStorage.setItem('reload', String(Date.now()));
      await this.getAuthInfo();
    },
    async sendMail(email: string) {
      return await sendMail(email);
    },
    async resetPass(pass: string, token: string) {
      return await resetPass(pass, token);
    },
    async changePass(oldPass: string, newPass: string) {
      return await changePass(oldPass, newPass);
    },
    async checkTokenInMail(token: string) {
      return await checkTokenInMail(token);
    },
    async refreshToken() {
      const response: any = await refreshToken();
      const { token } = response;
      localStorage.setItem('token', token);
      localStorage.setItem('token-lifetime', '0');
      localStorage.setItem('token-starttime', String(Date.now()));
      await this.getAuthInfo();
    },

    async signUp(userData: userCredentials) {
      return await signUp(userData);
    },

    async updateAccountStatus(token: string) {
      return await updateAccountStatus(token);
    }
  }
});
